/* eslint-disable max-len */
import { useCallback, useMemo } from 'react';
import { irsTerms, buildSpreadDiffStartDateLegs, parseBxtSxtInput, popupSpecsForDes, popupSpecsForQuickPrice, popupSpecsForOvml, PAGE_KEYS, addSpaceBeforeUnit } from 'lib/constants';
import { openTrackedWindow } from 'services/window-geometry.service';
import parsePlot, { parseFxoPlot, parseUnwind } from 'lib/getFormattedLegs';
import { socketEmit } from 'services/socket';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { getAuth } from 'services/auth.service';
import { nanoid } from 'nanoid';
import { normalizeFxCurrency, getLastFxfwdCurrency, setLastFxfwdCurrency, buildFxPlotUrl, parsePlotFxPhrase } from 'services/fx.service';
import { FX_DEFAULT_FIELD } from 'lib/fx-plot-constants';
import { parseCtascanArgument, resolveCtascanUrl } from 'services/ctascan-parser.service';
import useCtascanCatalog from 'hooks/useCtascanCatalog';
import { getIrsCta } from 'services/cta.service';
import { getLastOmonCurrency } from 'services/user-preference.service';
import useWorkspaceStore from 'store/workspaceStore';
import { markSkipNextWorkspaceHistory } from 'lib/workspace';

const flattenPlotTenors = (tenors) => {
    if (!tenors) return [];
    if (Array.isArray(tenors)) {
        return tenors.flatMap((t) => (Array.isArray(t) ? t : [t]))
            .map((t) => String(t).trim())
            .filter(Boolean);
    }
    return String(tenors).split(',').map((t) => t.trim()).filter(Boolean);
};

const trackWorkspaceFromSearch = async (payload) => {
    if (!payload?.curveId || !payload?.curveName || !payload?.command) return null;
    const tile = await useWorkspaceStore.getState().trackExecution(payload);
    const cmd = String(payload.command || '').toUpperCase();
    if (tile) {
        markSkipNextWorkspaceHistory(payload.curveId, cmd);
    }
    return tile;
};

const DEFAULT_CURVE = 'USD OIS ANN VS SOFR';
const DEFAULT_TENOR = '5Y';

function normalizeLegsParam(raw) {
    if (raw == null || raw === '') return DEFAULT_TENOR;
    const normalized = Array.isArray(raw)
        ? raw.map((t) => String(t).trim().toLowerCase()).filter(Boolean).join(',')
        : String(raw).trim().toLowerCase();
    if (!normalized) return DEFAULT_TENOR;
    const isValid = (val) => /^\d+(?:\.\d+)?[dwmy]$/i.test(val);
    if (normalized.includes(',')) {
        const parts = normalized
            .split(',')
            .map((p) => p.trim())
            .filter(Boolean)
            .map((p) => (isValid(p) ? p : DEFAULT_TENOR));

        return parts.length ? parts.join(',') : DEFAULT_TENOR;
    }

    return isValid(normalized) ? normalized : DEFAULT_TENOR;
}

const openInSamePage = (url) => {
    window.open(url, '_self');
};

const openTargetPage = (url) => {
    window.open(url, nanoid(5), popupSpecsForDes);
};

function parseGip(command) {
    const match = command.match(/\b\d+\b/);
    return match ? Number(match[0]) : null;
}

const getMultiPlotCommandSegmentStrings = (trimmedPhrase, commandPrefix) => {
    const commandRegex = new RegExp(`^${commandPrefix}\\b`, 'i');
    if (!commandRegex.test(trimmedPhrase)) return null;
    const afterCommand = trimmedPhrase.replace(new RegExp(`^${commandPrefix}\\s*`, 'i'), '').trim();
    if (!afterCommand.includes(',')) return null;
    const segments = afterCommand
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    if (segments.length < 2) return null;
    return segments;
};

const splitCommaSeparatedStructures = (afterCommand) => afterCommand
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const parseMultiPlotCommandParsedList = (segmentStrings, commandPrefix, validImm, isIrsPage) => segmentStrings.map((seg) => parsePlot(`${commandPrefix} ${seg}`, validImm, isIrsPage));

function handleTraceChronosBranch({
    searchPhrase,
    isIrsPage,
    irsId,
    chronos,
    openInSamePage,
    fallbackName = '',
    irs
}) {
    if (searchPhrase.trim().toLowerCase().startsWith('trace') || searchPhrase.trim().toLowerCase().endsWith('trace')) {
        if (isIrsPage) {
            const toLower = searchPhrase.trim().toLowerCase();
            if (toLower === 'trace') {
                chronos({});
                return true;
            }
        }
        const HAS_ALL_REGEX = /\ball\b/i;
        const TENOR_LIST_REGEX = /\b\d+[ymwd](?:\/\d+[ymwd])*\b/i;
        let normalizedText = searchPhrase.toLowerCase()?.replace('spot', '').trim();
        if (HAS_ALL_REGEX.test(normalizedText)) {
            const hasCurveTenor = /\b\d+[ymdw]\d+[ymdw]\b/i.test(normalizedText);

            if (hasCurveTenor) {
                return false;
            }

            normalizedText = normalizedText
                .replace(/\bfwd\b.*$/i, '')
                .replace(/\ball\b/i, '')
                .replace(/\s+/g, ' ')
                .trim();

            const tenorMatch = normalizedText.match(TENOR_LIST_REGEX);

            if (tenorMatch) {
                const validTenors = tenorMatch[0]
                    .split('/')
                    .join('/');

                normalizedText = normalizedText.replace(TENOR_LIST_REGEX, validTenors);
            }

            const parts = normalizedText.split(' ');
            const hasExplicitTenor = Boolean(tenorMatch);

            if (!hasExplicitTenor) {
                if (parts.length === 1 && ['chronos', 'trace'].includes(parts[0])) {
                    normalizedText = `${parts[0]} *`;
                } else if (!normalizedText.endsWith('*')) {
                    normalizedText = `${normalizedText} *`;
                }
            }
            normalizedText += ' fwd *';
        }

        const afterCommand = normalizedText.replace(/^(chronos|trace)\s*/i, '');
        const meetingElement = /(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC|RBA|FED|BOC|SNB|ECB|BOE|BOJ|NZD|MEET)(\d{2})?(\s*[A-Za-z]*)?(\s*\([A-Za-z]+\))?/i;
        const structElement = /(?:\*|[zhum]\d+[a-z]?(?:(?:\s+|\s*)\d+(?:\.\d+)?[ymdw])+|[zhum]\d+[a-z]?(?:\s*\d+(?:\.\d+)?[ymdw])?|(?:[zhum]\d+[a-z]?|\d+(?:\.\d+)?[ymdw])(?:\s*[,\-]\s*(?:[zhum]\d+[a-z]?|\d+(?:\.\d+)?[ymdw]))*|\d+(?:\.\d+)?[ymdw](?:(?:\s*,?\s*|\s+)\d+(?:\.\d+)?[ymdw])*|\d+(?:\.\d+)?[ymdw]\-\d+(?:\.\d+)?[ymdw])/i;
        const combinedElement = `(?:${meetingElement.source}|${structElement.source})`;
        const structurePattern = new RegExp(`^(?:${combinedElement}(?:\\s+fwd\\s+${combinedElement})?)$`, 'i');
        let beforeStructure = '';
        let structure = '';

        if (structurePattern.test(afterCommand)) {
            beforeStructure = '';
            structure = afterCommand;
        } else {
            const words = afterCommand.split(/\s+/);

            for (let i = 0; i < words.length; i++) {
                const potentialStructure = words.slice(i).join(' ');
                if (structurePattern.test(potentialStructure)) {
                    beforeStructure = words.slice(0, i).join(' ');
                    structure = potentialStructure;
                    break;
                }
            }
        }

        let name = beforeStructure.trim()?.toLowerCase() || fallbackName;

        if (['0d', '0m', '0y', 'spot'].includes(name)) name = '';
        if (isIrsPage && irsId && !name && structure) {
            openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}chronos/${irsId}?str=${structure}`);
            return true;
        }
        const contextCurveName = (isIrsPage && irs?.name) ? String(irs.name).toLowerCase() : '';
        name = contextCurveName || DEFAULT_CURVE;
        if (!name) return false;

        socketEmit('/plots/search', { command: 'CHRONOS', phrase: name, structure: structure || DEFAULT_TENOR, tenors: [] });
        return true;
    }
    return false;
}

export function createDefaultIrsCommandHandlers({
    irs,
    irsId: irsIdFallback,
    router
}) {
    const id = irs?.id ?? irsIdFallback;
    const base = process.env.NEXT_PUBLIC_UI_URL;

    const fxfwd = ({ currency } = {}) => {
        const auth = getAuth();
        const userId = auth?.id || null;

        let target = null;
        if (currency) {
            const { bare, valid } = normalizeFxCurrency(currency);
            if (!valid) {
                toast(`Invalid currency for FXFWD: ${currency}`, { type: 'error', toastId: 'fxfwd-invalid' });
                return;
            }
            target = bare;
            setLastFxfwdCurrency(userId, target);
        } else {
            target = getLastFxfwdCurrency(userId) || 'INR';
        }

        openInSamePage(`${base}fwcm/ndf/${target}?command=fwcm`);
    };
    const basis = (data) => {
        if (!data?.depId || !data?.indepId) return;
        const l1Str = normalizeLegsParam(data?.l1);
        const l2Str = normalizeLegsParam(data?.l2 != null ? data.l2 : data?.l1);
        const params = new URLSearchParams({
            command: 'BASIS',
            depId: String(data.depId),
            indepId: String(data.indepId),
            l1: l1Str,
            l2: l2Str,
            legs: l1Str
        });
        if (data.depF) params.set('depF', String(data.depF));
        if (data.indepF) params.set('indepF', String(data.indepF));
        if (data.depImm) params.set('depImm', String(data.depImm));
        if (data.indepImm) params.set('indepImm', String(data.indepImm));
        if (data.immFwd) params.set('immFwd', String(data.immFwd));
        if (data.imm) params.set('imm', String(data.imm));
        if (data.plotType) params.set('plotType', String(data.plotType));
        if (data.immLabel) params.set('immLabel', String(data.immLabel));
        if (data.immLabel3) params.set('immLabel3', String(data.immLabel3));
        const pathId = encodeURIComponent(String(data.depId));
        openInSamePage(`${base}plot/irs/${pathId}?${params.toString()}`);
    };

    const meet = ({ irsId: payloadIrsId } = {}) => {
        const resolvedId = payloadIrsId ?? id;
        if (!resolvedId) return;
        const { legs } = router.query || {};
        let url = `${base}meet?id=${resolvedId}`;
        if (legs) {
            url += `&legs=${legs}`;
        }
        openInSamePage(url);
    };

    const arc = (data) => {
        const arcId = String(data?.irsId || id);
        const curveName = String(irs?.name || '').trim();
        if (curveName) {
            trackWorkspaceFromSearch({
                curveId: arcId,
                curveName,
                structure: null,
                command: 'ARC'
            });
        }
        let qs = '';
        if (data?.bobCurve) qs += `?bobCurve=${data.bobCurve}`;
        if (data?.basis6s3sCurve) qs += `${data?.bobCurve ? '&' : '?'}basis6s3sCurve=${data.basis6s3sCurve}`;
        openInSamePage(`${base}arc/irs/${arcId}${qs}`);
    };

    const plotfx = ({ currency, structure, field = FX_DEFAULT_FIELD, range, mode, ovdvCurrency } = {}) => {
        if (mode === 'spot') {
            let effCcy = currency;
            if (!effCcy) {
                const auth = getAuth();
                effCcy = getLastFxfwdCurrency(auth?.id || null) || 'INR';
            }
            const { bare, valid } = normalizeFxCurrency(effCcy);
            if (!valid) {
                toast(`Invalid currency for PLOTFX: ${effCcy}`, { type: 'error', toastId: 'plotfx-invalid' });
                return;
            }
            const auth = getAuth();
            setLastFxfwdCurrency(auth?.id || null, bare);
            openInSamePage(`${base}plot/fx/${bare}?mode=spot`);
            return;
        }
        if (!structure) return;
        let effectiveCurrency = currency;
        if (!effectiveCurrency) {
            const asPath = router?.asPath || '';
            const fxRouteMatch = asPath.match(/\/(?:plot\/fx|fwcm\/ndf|fxfwd(?:\/ndf)?)\/([A-Za-z]{3})\b/);
            if (fxRouteMatch) effectiveCurrency = fxRouteMatch[1];
        }
        if (!effectiveCurrency && (irs || ovdvCurrency)) {
            const candidates = [irs?.currency, ovdvCurrency];
            for (const c of candidates) {
                if (!c) continue;
                const { bare, valid } = normalizeFxCurrency(String(c).split(/\s+/)[0]);
                if (valid) { effectiveCurrency = bare; break; }
            }
        }
        if (!effectiveCurrency) {
            const auth = getAuth();
            effectiveCurrency = getLastFxfwdCurrency(auth?.id || null) || 'INR';
        }
        const { bare, valid } = normalizeFxCurrency(effectiveCurrency);
        if (!valid) {
            toast(`Invalid currency for PLOTFX: ${effectiveCurrency}`, { type: 'error', toastId: 'plotfx-invalid' });
            return;
        }
        const auth = getAuth();
        setLastFxfwdCurrency(auth?.id || null, bare);
        const url = buildFxPlotUrl({ currency: bare, structure, field, range, origin: base });
        if (url) openInSamePage(url);
    };

    const cdrHoliday = ({ code } = {}) => {
        openInSamePage(`${base}cdr-holidays${code ? `?code=${encodeURIComponent(code.toUpperCase())}` : ''}`);
    };

    if (!id) {
        return {
            arc,
            basis,
            fxfwd,
            plotfx,
            cdrHoliday,
            trades: () => openInSamePage(`${base}trades`),
            meet
        };
    }

    return {
        plot: async ({ legs, spot, isFwd, imm, secondImm, plotType, thirdImm, command, plotaSeries = [] } = {}) => {
            if (spot) return;
            const curveName = String(irs?.name || '').trim();
            if (curveName) {
                const buildImmWorkspaceStructure = () => {
                    if (!isFwd) return null;
                    const starts = [imm?.label, secondImm?.label, thirdImm?.label]
                        .filter(Boolean)
                        .map((s) => String(s).toUpperCase().trim());
                    if (!starts.length) return null;
                    const tokens = String(legs || '')
                        .split(',')
                        .map((t) => String(t).trim().toUpperCase())
                        .filter(Boolean);
                    // Forward legs come as [start,end,start,end,...] where start may be a year/decimal.
                    const ends = tokens.filter((_, idx) => idx % 2 === 1)
                        .map((t) => t.replace(/(\d+(?:\.\d+)?)\s*([DWMY])/i, (_, v, u) => `${v}${u.toUpperCase()}`));
                    if (!ends.length) return null;
                    const orderedStarts = starts.slice(0, ends.length);
                    if (orderedStarts.length > 1 && orderedStarts.length === ends.length) orderedStarts.reverse();
                    const fallbackStart = orderedStarts[orderedStarts.length - 1] || orderedStarts[0];
                    return ends.map((end, idx) => `${orderedStarts[idx] || fallbackStart} FWD ${end}`).join(' ');
                };
                const flatLegs = flattenPlotTenors(legs);
                const structureForWorkspace = buildImmWorkspaceStructure()
                    || (flatLegs.length
                        ? formatWorkspaceStructureFromLegTerms(flatLegs, { isFwdCurve: Boolean(isFwd) })
                        : null);
                await trackWorkspaceFromSearch({
                    curveId: String(id),
                    curveName,
                    structure: structureForWorkspace,
                    command: command === 'PLOTA' ? 'PLOTA' : 'PLOT',
                    isFwdCurve: Boolean(isFwd)
                });
            }
            const extraPlotaQs = Array.isArray(plotaSeries) && plotaSeries.length ? `&plotaSeries=${encodeURIComponent(plotaSeries)}` : '';
            const url = `${base}plot/irs/${id}?legs=${legs}${isFwd ? '&command=fwcm' : ''}${imm ? `&immFwd=${imm.label}` : ''}${secondImm ? `&imm=${secondImm.y}y&immLabel=${secondImm.label}` : ''}${thirdImm ? `&immLabel3=${thirdImm.label}` : ''}${plotType ? `&plotType=${plotType}` : ''}${command === 'PLOTA' ? '&advanced=true' : ''}${extraPlotaQs}`;
            openInSamePage(url);
        },
        plotF: (legs, spot, plotfSeries = []) => {
            if (spot) return;
            const extraPlotfQs = Array.isArray(plotfSeries) && plotfSeries.length ? `&plotfSeries=${encodeURIComponent(plotfSeries)}` : '';
            openInSamePage(`${base}plotf/irs/${id}?legs=${legs}${extraPlotfQs}`);
        },
        fwcm: ({ irsId: oid, bobCurve, basis6s3sCurve } = {}) => {
            let qs = '';
            if (bobCurve) qs += `?bobCurve=${bobCurve}`;
            if (basis6s3sCurve) qs += `${bobCurve ? '&' : '?'}basis6s3sCurve=${basis6s3sCurve}`;
            openInSamePage(`${base}fwcm/irs/${oid || id}${qs}`);
        },
        fxfwd,
        plotfx,
        edge: () => {
            const curveName = String(irs?.name || '').trim();
            if (curveName) {
                trackWorkspaceFromSearch({
                    curveId: String(id),
                    curveName,
                    structure: null,
                    command: 'EDGE'
                });
            }
            openInSamePage(`${base}edge/irs/${id}`);
        },
        cdrHoliday,
        stats: () => openInSamePage(`${base}stats/irs/${id}`),
        curveCmd: () => openInSamePage(`${base}curve/${id}`),
        arc,
        chronos: ({ legs }) => {
            const curveName = String(irs?.name || '').trim();
            if (curveName) {
                trackWorkspaceFromSearch({
                    curveId: String(id),
                    curveName,
                    structure: legs || DEFAULT_TENOR,
                    command: 'TRACE'
                });
            }
            openInSamePage(`${base}chronos/${id}?${legs ? `str=${legs}` : `str=${DEFAULT_TENOR}`}`);
        },
        anchorCmd: () => {
            const curveName = String(irs?.name || '').trim();
            if (curveName) {
                trackWorkspaceFromSearch({
                    curveId: String(id),
                    curveName,
                    structure: null,
                    command: 'ANCHOR'
                });
            }
            openInSamePage(`${base}anchor?depId=${id}&l1=${DEFAULT_TENOR}`);
        },
        otc: () => openInSamePage(`${base}otc/${irs?.currency || ''}`),
        trades: () => openInSamePage(`${base}trades`),
        ctascan: () => openInSamePage(`${base}cta`),
        meet,
        basis
    };
}

export function useIrsGlobalSearchEnterCommand({
    irs,
    irsId: irsIdProp = '',
    isIrsPage,
    isBondPage,
    curve,
    validImm,
    forDetailsPage,
    isOTCPage,
    legs,
    immFwd,
    hasSpreadDiffStartDate,
    irsTerm,
    ovdvCurrency = null,
    valid6s3sBobCcySet,
    setSearchPhrase,
    resetCursor,
    isCorpChildComponent,
    setTicker,
    setCnPhrase = () => { },
    getHighlightedItemData,
    commandOverrides = {},
    setShowLoader,
    basisReferenceIrsId = null
}) {
    const noop = useMemo(() => () => { }, []);
    const router = useRouter();
    const auth = useMemo(() => getAuth(), []);

    const defaults = useMemo(
        () => createDefaultIrsCommandHandlers({
            irs,
            irsId: irsIdProp,
            router
        }),
        [irs, irsIdProp, router]
    );

    const irsId = irs?.id ?? irsIdProp;

    const ctascanCatalog = useCtascanCatalog();
    const base = process.env.NEXT_PUBLIC_UI_URL;

    const ctascanHandler = useCallback(async (payload = {}) => {
        const arg = (payload && payload.argument) || '';
        let parsed = parseCtascanArgument(arg, ctascanCatalog);

        if (arg.trim() && parsed.kind === 'grid' && !(ctascanCatalog.irsCurves?.length)) {
            try {
                const resp = await getIrsCta({ debug: false });
                if (resp?.status) {
                    const entity = resp?.data?.entity || {};
                    const sectors = Object.keys(entity);
                    const irsCurves = [];
                    sectors.forEach((sector) => {
                        (entity[sector] || []).forEach((row) => {
                            if (!row || !row.curve) return;
                            irsCurves.push({ name: row.curve, ccy: row.ccy, irsId: row.irsId, sector });
                        });
                    });
                    parsed = parseCtascanArgument(arg, { ...ctascanCatalog, sectors, irsCurves });
                }
            } catch (e) {
                console.warn('[ctascan] on-demand getIrsCta failed', e);
            }
        }

        const path = resolveCtascanUrl(parsed, { uiBase: '/' });
        const sep = path.includes('?') ? '&' : '?';
        router.push(`${path}${sep}_v=${Date.now()}`);
    }, [ctascanCatalog, router]);

    // Bare OVML opens blank — no pair to resolve, no session/preference lookup,
    // unlike OMON/OVDV which always resolve to some currency.
    const ovmlCmd = useCallback(() => {
        window.open(`${process.env.NEXT_PUBLIC_UI_URL}ovml`, nanoid(5), popupSpecsForOvml);
    }, []);

    const effectiveHandlers = useMemo(() => ({
        ...(defaults || {}),
        ctascan: ctascanHandler,
        ovml: ovmlCmd,
        ...(commandOverrides || {})
    }), [
        defaults,
        ctascanHandler,
        ovmlCmd,
        commandOverrides
    ]);

    const {
        plot = noop,
        plotF = noop,
        fwcm = noop,
        fxfwd = noop,
        plotfx = noop,
        edge = noop,
        stats = noop,
        curveCmd = noop,
        arc = noop,
        chronos = noop,
        anchorCmd = noop,
        basis = noop,
        gip = noop,
        otc = noop,
        meet = noop,
        trades = noop,
        ctascan = noop,
        cdrHoliday = noop,
        ovml = noop
    } = effectiveHandlers;

    const runCommand = useCallback((command, payload) => {
        const key = command?.toUpperCase();
        switch (key) {
            case 'PLOT':
                return plot(payload);
            case 'PLOTF':
                return plotF(payload);
            case 'FWCM':
                return fwcm(payload);
            case 'FXFWD':
                return fxfwd(payload);
            case 'PLOTFX':
                return plotfx(payload);
            case 'EDGE':
                return edge(payload);
            case 'STATS':
                return stats(payload);
            case 'CURVE':
            case 'CURVECMD':
                return curveCmd(payload);
            case 'ARC':
                return arc(payload);
            case 'TRACE':
                return chronos(payload);
            case 'ANCHOR':
            case 'ANCHORCMD':
                return anchorCmd(payload);
            case 'BASIS':
                return basis(payload);
            case 'GIP':
                return gip(payload);
            case 'OTC':
                return otc(payload);
            case 'MEET':
                return meet(payload);
            case 'TRADES':
                return trades(payload);
            case 'CTASCAN':
                return ctascan(payload);
            case 'CDR':
                return cdrHoliday(payload);
            case 'OVML':
                return ovml(payload);
            default:
                return undefined;
        }
    }, [plot, plotF, fwcm, fxfwd, edge, stats, curveCmd, arc, chronos, anchorCmd, basis, gip, otc, meet, trades, ctascan, cdrHoliday, ovml]);

    const executeIrsEnterCommand = useCallback((searchPhrase, fallbackName) => {
        const isFxoForced = /^\s*(plotfxo|plotvol)\b/i.test(searchPhrase);
        if (/^\s*(plotfxo|plotvol)\s*$/i.test(searchPhrase)) {
            searchPhrase = `plot ${ovdvCurrency || 'USDINR'} 1M ATM`;
        } else {
            searchPhrase = searchPhrase.replace(/^\s*(plotfxo|plotvol)(\s|$)/i, 'plot$2');
        }
        const contextCurveName = (isIrsPage && irs?.name) ? String(irs.name).toLowerCase() : '';
        const defaultCurveName = contextCurveName || DEFAULT_CURVE;

        if (searchPhrase.trim().toLowerCase().startsWith('newsfeed')) {
            const url = `${process.env.NEXT_PUBLIC_UI_URL}newsfeed`;
            openInSamePage(url);
            setSearchPhrase('');
            resetCursor();
            return true;
        }
        if (searchPhrase.trim().toLowerCase() === 'ndle') {
            const isIdbBroker = auth?.role === 'Broker' && auth?.traderType === 'IDBBroker';
            const isSuperAdmin = auth?.role === 'SuperAdmin';
            if (!isIdbBroker && !isSuperAdmin) {
                toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' });
                setSearchPhrase('');
                resetCursor();
                return true;
            }
            openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}ndle`);
            setSearchPhrase('');
            resetCursor();
            return true;
        }
        if (searchPhrase.trim().toLowerCase() === 'watch' || searchPhrase.trim().toLowerCase().startsWith('monitor') || searchPhrase.trim().toLowerCase().startsWith('watchlist')) {
            const url = `${process.env.NEXT_PUBLIC_UI_URL}watchlist`;
            openTrackedWindow(PAGE_KEYS.WATCHLIST, url, popupSpecsForDes);
            setSearchPhrase('');
            resetCursor();
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('surface')) {
            openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}surface`);
            setSearchPhrase('');
            resetCursor();
            return true;
        }
        if (searchPhrase.trim().toLowerCase() === 'cdr' || searchPhrase.trim().toLowerCase().startsWith('cdr ')) {
            const code = searchPhrase.trim().slice(3).trim();
            const url = `${process.env.NEXT_PUBLIC_UI_URL}cdr-holidays${code ? `?code=${encodeURIComponent(code.toUpperCase())}` : ''}`;
            if (forDetailsPage) openInSamePage(url);
            else openTargetPage(url);
            setSearchPhrase('');
            resetCursor();
            return true;
        }
        {
            const lower = searchPhrase.trim().toLowerCase();
            if (lower === 'unwind' || lower === 'tear' || lower.startsWith('unwind ') || lower.startsWith('tear ')) {
                const parsed = parseUnwind(searchPhrase);
                let curveId = irsId || 'new';
                if (parsed.currency && Array.isArray(ctascanCatalog.irsCurves)) {
                    const match = ctascanCatalog.irsCurves.find((c) => (c.ccy || '').toUpperCase() === parsed.currency);
                    if (match?.irsId) curveId = match.irsId;
                }
                const qs = new URLSearchParams();
                if (parsed.structureSpec) qs.set('spec', parsed.structureSpec);
                if (parsed.side && parsed.side !== 'pay') qs.set('side', parsed.side);
                if (parsed.origLvl != null) qs.set('orig', String(parsed.origLvl));
                if (parsed.unwindLvl != null) qs.set('unwind', String(parsed.unwindLvl));
                if (parsed.notional != null && parsed.notional !== 100e6) qs.set('notional', String(parsed.notional));
                if (parsed.currency && curveId === 'new') qs.set('ccy', parsed.currency);
                const url = `${process.env.NEXT_PUBLIC_UI_URL}unwind/${curveId}${qs.toString() ? `?${qs.toString()}` : ''}`;
                openInSamePage(url);
                setSearchPhrase('');
                resetCursor();
                return true;
            }
        }
        if (searchPhrase.trim().toLowerCase().startsWith('atlas') || searchPhrase.trim().toLowerCase().startsWith('sdr')) {
            const search = searchPhrase.trim().toLowerCase();
            const keywords = search.replace(/^(atlas|sdr)\s*/, '');
            let tab = '';
            if (/(^fx|^fxo|^fx-|fx option|fx-options)/.test(keywords)) {
                tab = 'fx-options';
            } else if (/(^rate|^rates|interest|irs)/.test(keywords)) {
                tab = 'irs';
            }
            const baseUrl = `${process.env.NEXT_PUBLIC_UI_URL}atlas`;
            const url = tab ? `${baseUrl}?tab=${tab}` : baseUrl;
            openTrackedWindow(PAGE_KEYS.ATLAS, url, popupSpecsForQuickPrice);
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('ctascan')) {
            const arg = searchPhrase.trim().slice('ctascan'.length).trim();
            runCommand('CTASCAN', { argument: arg });
            setSearchPhrase('');
            resetCursor();
            return true;
        }
        if (searchPhrase.toLowerCase().includes('corp')) {
            const words = searchPhrase.trim().toLowerCase().split(/\s+/);
            const ticker = words.find((word) => word !== 'corp');
            if (!ticker) return true;
            if (isCorpChildComponent) {
                setTicker(ticker);
            } else {
                if (ticker) {
                    const url = `${process.env.NEXT_PUBLIC_UI_URL}corp/${ticker}`;
                    openInSamePage(url);
                    return true;
                }
                toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' });
                return true;
            }
        }
        if (searchPhrase.toLowerCase().includes('cn') && isCorpChildComponent) {
            const words = searchPhrase.trim().toLowerCase().split(/\s+/);
            const phrase = words.find((word) => word !== 'cn');
            setCnPhrase(phrase || 'USD');
        }

        const itemData = getHighlightedItemData?.();

        if (!itemData && (searchPhrase.toLowerCase().trim() === 'otc'
            || searchPhrase.toLowerCase().startsWith('otc ') || searchPhrase.toLowerCase().endsWith(' otc'))) {
            const index = searchPhrase.toLowerCase().trim().startsWith('otc') ? 1 : 0;
            const currency = searchPhrase.trim().split(' ')[index]?.toLowerCase();
            if (searchPhrase.toLowerCase().trim() === 'otc' && otc) {
                otc();
                return true;
            }
            if (currency) {
                const url = `${process.env.NEXT_PUBLIC_UI_URL}otc/${currency}`;
                openInSamePage(url);
                return true;
            }
            toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' });
            return true;
        }

        if (isIrsPage && (searchPhrase.toLowerCase().trim() === 'gip' || searchPhrase.toLowerCase().startsWith('gip '))) {
            gip(parseGip(searchPhrase));
            setSearchPhrase('');
            return true;
        }
        if (!isBondPage && searchPhrase.toLowerCase().startsWith('bxt')) {
            if (auth.role !== 'Broker') {
                toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' });
                return true;
            }
            const {
                curveName, tenors, start, dv01s, sizes, trader, isFwd, isValid,
                hasSpreadDiffStartDate: parsedHasSpreadDiffStartDate,
                spreadDiffStartData: parsedSpreadDiffStartData
            } = parseBxtSxtInput(searchPhrase?.trim(), validImm);
            if (tenors?.length > 3 || !isValid) return false;
            const phrase = curveName || (isIrsPage && curve);
            const merged = [...irsTerms, ...validImm];

            const filtered = merged.filter((term) => legs?.includes(term));
            const effectiveHasSpreadDiffStartDate = (hasSpreadDiffStartDate && searchPhrase.trim().toLowerCase() === 'bxt') || parsedHasSpreadDiffStartDate;
            const startLabel = start || immFwd || 'Spot';
            const legsObj = {};
            legs.forEach((value, index) => {
                legsObj[`l${index + 1}`] = value;
            });
            const spreadDiffStartData = effectiveHasSpreadDiffStartDate
                ? (parsedSpreadDiffStartData || buildSpreadDiffStartDateLegs({ legs: legsObj, imm: validImm }))
                : {};
            const cleanTenors = tenors?.length ? tenors : (filtered?.length ? filtered : [irsTerm || '1 Y']);
            socketEmit('/plots/search', {
                phrase,
                tenors: effectiveHasSpreadDiffStartDate
                    ? [
                        addSpaceBeforeUnit(spreadDiffStartData?.endLabel),
                        addSpaceBeforeUnit(spreadDiffStartData?.end1Label),
                        ...(spreadDiffStartData?.end2Label ? [addSpaceBeforeUnit(spreadDiffStartData?.end2Label)] : [])
                    ]
                    : cleanTenors,
                start: startLabel,
                command: 'BXT',
                dv01s,
                sizes,
                trader,
                isFwd,
                hasSpreadDiffStartDate: effectiveHasSpreadDiffStartDate,
                ...(effectiveHasSpreadDiffStartDate && { spreadDiffStartData })
            });
            return true;
        }
        if (!isBondPage && searchPhrase.toLowerCase().startsWith('sxt')) {
            if (auth.role !== 'Broker') {
                toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' });
                return true;
            }
            const {
                curveName, tenors, start, dv01s, sizes, trader, isFwd, isValid,
                hasSpreadDiffStartDate: parsedHasSpreadDiffStartDate,
                spreadDiffStartData: parsedSpreadDiffStartData
            } = parseBxtSxtInput(searchPhrase?.trim(), validImm);
            if (tenors?.length > 3 || !isValid) return false;
            const phrase = curveName || (isIrsPage && curve);
            const merged = [...irsTerms, ...validImm];

            const filtered = merged.filter((term) => legs?.includes(term));
            const effectiveHasSpreadDiffStartDate = (hasSpreadDiffStartDate && searchPhrase.trim().toLowerCase() === 'sxt') || parsedHasSpreadDiffStartDate;
            const startLabel = start || immFwd || 'Spot';
            const legsObj = {};
            legs.forEach((value, index) => {
                legsObj[`l${index + 1}`] = value;
            });
            const spreadDiffStartData = effectiveHasSpreadDiffStartDate
                ? (parsedSpreadDiffStartData || buildSpreadDiffStartDateLegs({ legs: legsObj, imm: validImm }))
                : {};
            const cleanTenors = tenors?.length ? tenors : (filtered?.length ? filtered : [irsTerm || '1 Y']);
            socketEmit('/plots/search', {
                phrase,
                tenors: effectiveHasSpreadDiffStartDate
                    ? [
                        addSpaceBeforeUnit(spreadDiffStartData?.endLabel),
                        addSpaceBeforeUnit(spreadDiffStartData?.end1Label),
                        ...(spreadDiffStartData?.end2Label ? [addSpaceBeforeUnit(spreadDiffStartData?.end2Label)] : [])
                    ]
                    : cleanTenors,
                start: startLabel,
                command: 'SXT',
                dv01s,
                sizes,
                trader,
                isFwd,
                hasSpreadDiffStartDate: effectiveHasSpreadDiffStartDate,
                ...(effectiveHasSpreadDiffStartDate && { spreadDiffStartData })
            });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('basis')) {
            const toLower = searchPhrase.trim().toLowerCase();
            const basisIndepId = basisReferenceIrsId || irsId;
            if (toLower === 'basis' && irsId) {
                setSearchPhrase('');
                basis({ depId: irsId, indepId: basisIndepId, withoutLegs: true });
                return true;
            }
            const basisSegments = splitCommaSeparatedStructures(toLower.replace('basis', ''));
            const rawStructure1 = basisSegments[0] ?? DEFAULT_TENOR;
            const rawStructure2 = basisSegments.length > 1 ? basisSegments[1] : null;

            const res1 = parsePlot(`plot ${rawStructure1}`, validImm, true);
            const res2 = rawStructure2 == null
                ? { ...res1, name: '' }
                : parsePlot(`plot ${rawStructure2}`, validImm, true);
            if (!res1.isValid || !res2.isValid) {
                return false;
            }

            const phrase1 = res1.name || fallbackName || defaultCurveName;
            const phrase2 = res2.name || fallbackName || DEFAULT_CURVE;

            const { name, tenors, spot, isFwd, imm, immFwd, plotType, immLabel, secondImm } = res1;

            if (!res1.name && !res2.name && irsId && basisReferenceIrsId) {
                const params = {
                    l1: tenors,
                    depId: irsId,
                    l2: res2.tenors,
                    indepId: basisReferenceIrsId
                };
                if (imm) params.depImm = '1';
                const immFwdLabel = immFwd || imm?.label;
                if (immFwdLabel) params.immFwd = immFwdLabel;
                if (plotType) params.plotType = plotType;
                const immLabelResolved = immLabel || secondImm?.label;
                if (immLabelResolved) params.immLabel = immLabelResolved;
                if (isFwd || imm) params.depF = '1';
                if (res2.imm) params.indepImm = '1';
                if (res2.isFwd || res2.imm) params.indepF = '1';
                setSearchPhrase('');
                basis(params);
                return true;
            }
            socketEmit('/plots/search', { command: 'BASIS', phrase: phrase1, tenors, spot, isFwd, imm, checkIfMaybeCds: false, uid: 'reg1' });
            socketEmit('/plots/search', { command: 'BASIS', phrase: phrase2, tenors: res2.tenors, spot: res2.spot, isFwd: res2.isFwd, imm: res2.imm, checkIfMaybeCds: false, uid: 'reg2' });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('regress')) {
            const toLower = searchPhrase.trim().toLowerCase().toLowerCase();
            if (toLower === 'regress' && irsId) {
                const url = `${process.env.NEXT_PUBLIC_UI_URL}regress?depId=${irsId}&indepId=${irsId}&l1=${DEFAULT_TENOR}&l2=${DEFAULT_TENOR}`;
                if (curve) {
                    trackWorkspaceFromSearch({
                        curveId: String(irsId),
                        curveName: String(curve).trim(),
                        structure: null,
                        command: 'REGRESS',
                        isFwdCurve: false
                    });
                }
                openInSamePage(url);
            }
            const regressSegments = splitCommaSeparatedStructures(toLower.replace('regress', ''));
            const rawStructure1 = regressSegments[0] ?? DEFAULT_TENOR;
            const rawStructure2 = regressSegments.length > 1 ? regressSegments[1] : null;

            const res1 = parsePlot(`plot ${rawStructure1}`, validImm, true);
            const res2 = rawStructure2 == null
                ? { ...res1, name: '' }
                : parsePlot(`plot ${rawStructure2}`, validImm, true);
            if (!res1.isValid || !res2.isValid) {
                return false;
            }

            const phrase1 = res1.name || fallbackName || defaultCurveName;
            const phrase2 = res2.name || phrase1;

            const { tenors, spot, isFwd, imm } = res1;

            socketEmit('/plots/search', { command: 'REGRESS', phrase: phrase1, tenors, spot, isFwd, imm, checkIfMaybeCds: false, uid: 'reg1' });
            socketEmit('/plots/search', { command: 'REGRESS', phrase: phrase2, tenors: res2.tenors, spot: res2.spot, isFwd: res2.isFwd, imm: res2.imm, checkIfMaybeCds: false, uid: 'reg2' });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('thor')) {
            const toLower = searchPhrase.trim().toLowerCase().toLowerCase();
            if (toLower === 'thor' && irsId) {
                const url = `${process.env.NEXT_PUBLIC_UI_URL}thor?depId=${irsId}&indepId=${irsId}&l1=${DEFAULT_TENOR}&l2=${DEFAULT_TENOR}`;
                openInSamePage(url);
            }
            const thorSegments = splitCommaSeparatedStructures(toLower.replace('thor', ''));
            const rawStructure1 = thorSegments[0] ?? DEFAULT_TENOR;
            const rawStructure2 = thorSegments.length > 1 ? thorSegments[1] : null;

            const res1 = parsePlot(`plot ${rawStructure1}`, validImm, true);
            const res2 = rawStructure2 == null
                ? { ...res1, name: '' }
                : parsePlot(`plot ${rawStructure2}`, validImm, true);

            if (!res1.isValid || !res2.isValid) {
                return false;
            }

            if ([res1?.plotType, res2?.plotType].some((type) => type?.startsWith('fBfly'))) return true;

            const phrase1 = res1.name || fallbackName || defaultCurveName;
            const phrase2 = res2.name || phrase1;

            const { tenors, spot, isFwd, imm } = res1;

            socketEmit('/plots/search', { command: 'THOR', phrase: phrase1, tenors, spot, isFwd, imm, checkIfMaybeCds: false, uid: 'reg1' });
            socketEmit('/plots/search', { command: 'THOR', phrase: phrase2, tenors: res2.tenors, spot: res2.spot, isFwd: res2.isFwd, imm: res2.imm, checkIfMaybeCds: false, uid: 'reg2' });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('anchor')) {
            const toLower = searchPhrase.trim().toLowerCase();
            if (toLower === 'anchor' && irsId) {
                anchorCmd();
                return true;
            }
            const structure = toLower.replace('anchor', '');
            if (toLower === 'anchor' && !structure) structure = DEFAULT_TENOR;
            const res = parsePlot(`plot ${structure}`, validImm, true);

            if (!res.isValid) {
                return false;
            }

            if (res?.plotType?.startsWith('fBfly')) return true;

            const { name, tenors, spot, isFwd, imm } = res;

            if (!name && irsId) {
                const url = `${process.env.NEXT_PUBLIC_UI_URL}anchor?l1=${tenors}&depId=${irsId}${imm ? '&depImm=1' : ''}${(isFwd || imm) ? '&depF=1' : ''}`;
                openInSamePage(url);
                return true;
            }

            socketEmit('/plots/search', { command: 'ANCHOR', phrase: name || DEFAULT_CURVE, tenors, spot, isFwd, imm, checkIfMaybeCds: false, uid: 'reg1' });
            return true;
        }
        if (handleTraceChronosBranch({
            searchPhrase,
            isIrsPage,
            irsId,
            chronos,
            openInSamePage,
            irsTerm,
            fallbackName,
            irs
        })) {
            return true;
        }

        if (!searchPhrase.toLowerCase().includes('inp')
            && !searchPhrase.toLowerCase().startsWith('inp ')
        ) {
            let trimmedPhrase = searchPhrase.trim();
            if (['plot', 'plota', 'plotf'].includes(trimmedPhrase?.toLowerCase())) {
                trimmedPhrase += ` ${DEFAULT_TENOR}`;
            }
            const lowerPhrase = trimmedPhrase.toLowerCase();
            const plotaMultiSegments = getMultiPlotCommandSegmentStrings(trimmedPhrase, 'plota');
            const plotfMultiSegments = getMultiPlotCommandSegmentStrings(trimmedPhrase, 'plotf');
            const multiSegmentStrings = plotaMultiSegments || plotfMultiSegments;
            const multiCommandPrefix = plotaMultiSegments ? 'plota' : plotfMultiSegments ? 'plotf' : null;
            let multiPlotList = null;
            if (multiSegmentStrings && multiCommandPrefix) {
                multiPlotList = parseMultiPlotCommandParsedList(
                    multiSegmentStrings,
                    multiCommandPrefix,
                    validImm,
                    isIrsPage || lowerPhrase.startsWith(multiCommandPrefix)
                );
                if (!multiPlotList.every((r) => r.isValid)) {
                    toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' });
                    setSearchPhrase('');
                    return true;
                }
                if (multiCommandPrefix === 'plotf' && multiPlotList.some((r) => r.isFwd)) {
                    toast('Invalid input: PLOTF expects a spot tenor (e.g. 5Y), not a forward (5Y5Y).', { type: 'error' });
                    return true;
                }
            }
            const plotSeriesForUrl = multiSegmentStrings ? multiSegmentStrings.slice(1) : [];
            const parsedPlot = multiPlotList?.[0] ?? parsePlot(trimmedPhrase, validImm, isIrsPage);

            if (parsedPlot.isValid && !isFxoForced) {
                const { name, tenors, spot, isFwd, imm, checkIfMaybeCds, secondImm, plotType, thirdImm, isPlotF, isPlotA, isBasis6s3s, isBob, isMeet, label } = parsedPlot;
                const irsName = name || fallbackName;
                if (isMeet) {
                    socketEmit('/get/irs/meeting-date/:irsId', { irsId: null, label, tag: 'search' });
                    return true;
                }
                if (!isFwd && tenors.length > 3) { toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' }); return true; }
                if (isBasis6s3s || isBob) {
                    // if (!name && !irs?.currency) return false;
                    let currency = name || irs?.currency || 'AUD';
                    if (!valid6s3sBobCcySet.has(currency.toUpperCase()) && isBob) currency = 'AUD';
                    socketEmit('/basis-plots/search', { isBasis6s3s, isBob, currency: !isBob ? 'AUD' : currency, tenors, spot, isFwd, imm, checkIfMaybeCds, secondImm, plotType, thirdImm });
                    return true;
                }
                if (!irsName && (forDetailsPage || isOTCPage)) {
                    if (isPlotF) {
                        if (isFwd) {
                            toast('Invalid input: PLOTF expects a spot tenor (e.g. 5Y), not a forward (5Y5Y).', { type: 'error' });
                            return true;
                        }
                        plotF(tenors, spot, plotSeriesForUrl.length ? plotSeriesForUrl : undefined);
                        return true;
                    }
                    socketEmit('/plots/search', {
                        phrase: irs?.name || DEFAULT_CURVE,
                        tenors,
                        spot,
                        isFwd,
                        imm,
                        checkIfMaybeCds,
                        secondImm,
                        plotType,
                        thirdImm,
                        command: isPlotA ? 'PLOTA' : null,
                        ...(isPlotA && { plotaSeries: plotSeriesForUrl.length ? plotSeriesForUrl : undefined }),
                        ...(isPlotF && { plotfSeries: plotSeriesForUrl.length ? plotSeriesForUrl : undefined })
                    });
                    return true;
                }

                if (
                    /^(plot|roll|plotf|plota)\s+.+/i.test(searchPhrase)
                    && (fallbackName || !parseFxoPlot(searchPhrase, ovdvCurrency).isValid)
                ) {
                    const command = isPlotF ? 'PLOTF' : isPlotA ? 'PLOTA' : null;
                    if (isPlotF && isFwd) {
                        toast('Invalid input: PLOTF expects a spot tenor (e.g. 5Y), not a forward (5Y5Y).', { type: 'error' });
                        return false;
                    }
                    socketEmit('/plots/search', {
                        phrase: (!isIrsPage && !name) ? DEFAULT_CURVE : irsName,
                        tenors,
                        spot,
                        isFwd,
                        imm,
                        checkIfMaybeCds,
                        secondImm,
                        plotType,
                        thirdImm,
                        command,
                        ...(isPlotA && plotSeriesForUrl.length ? { plotaSeries: plotSeriesForUrl } : {}),
                        ...(isPlotF && plotSeriesForUrl.length ? { plotfSeries: plotSeriesForUrl } : {})
                    });

                    return true;
                }
            }
            if (parseFxoPlot(searchPhrase, ovdvCurrency).isValid && !fallbackName) {
                const { details } = parseFxoPlot(searchPhrase, ovdvCurrency);
                socketEmit('/get/fxo-security', {
                    currency: details.ticker || ovdvCurrency || 'USDINR',
                    priceValue: details.priceValue,
                    tenor: details.tenor,
                    metadata: {
                        command: details.command,
                        tradeType: details.tradeType,
                        delta: details.delta
                    }
                });
                return true;
            }
        }
        if ((searchPhrase.trim().toLowerCase() === 'bob' || searchPhrase.trim().toLowerCase() === '6s3s')) {
            const isBob = searchPhrase.trim().toLowerCase() === 'bob';
            let currency = irs?.currency || '';
            if (!valid6s3sBobCcySet.has(currency.toUpperCase()) && isBob) currency = 'AUD';
            socketEmit('/basis-plots/search', { isBasis6s3s: !isBob, isBob, currency: !isBob ? 'AUD' : currency, isFwcm: true });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('fxfwd')) {
            const argument = searchPhrase.trim().slice(5).trim();
            fxfwd(argument ? { currency: argument } : {});
            setSearchPhrase('');
            resetCursor();
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('plotfx')) {
            const parsed = parsePlotFxPhrase(searchPhrase);
            if (parsed.ok) {
                runCommand('PLOTFX', { currency: parsed.currency, structure: parsed.structure, field: parsed.field, mode: parsed.mode, ovdvCurrency });
            } else if (parsed.error === 'INVALID_CURRENCY') {
                toast(`Invalid currency for PLOTFX: ${parsed.token || ''}`, { type: 'error', toastId: 'plotfx-args' });
            } else if (parsed.error === 'INVALID_STRUCTURE') {
                toast(`Invalid structure for PLOTFX: ${parsed.token || ''}`, { type: 'error', toastId: 'plotfx-args' });
            } else if (parsed.error === 'INVALID_FIELD') {
                toast(`Invalid field for PLOTFX: ${parsed.token || ''} (use PTS / OUT / YLD / BASIS)`, { type: 'error', toastId: 'plotfx-args' });
            } else {
                toast('PLOTFX needs a currency and a tenor — e.g. PLOTFX USDINR 1Y', { type: 'error', toastId: 'plotfx-args' });
            }
            setSearchPhrase('');
            resetCursor();
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('fwcm') || searchPhrase.trim().toLowerCase().endsWith('fwcm')) {
            if (searchPhrase.trim().toLowerCase() === 'fwcm' && isIrsPage) fwcm({});
            else {
                const name = searchPhrase.trim().toLowerCase().replace('fwcm', '').trim() || defaultCurveName;
                socketEmit('/plots/search', { phrase: name, command: 'fwcm', tenors: [] });
            }
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('edge') || searchPhrase.trim().toLowerCase().endsWith('edge')) {
            if (searchPhrase.trim().toLowerCase() === 'edge' && isIrsPage && irsId) edge();
            else {
                const name = searchPhrase.trim().toLowerCase().replace('edge', '').trim() || defaultCurveName;
                socketEmit('/plots/search', { phrase: name, command: 'edge', tenors: [] });
            }
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('stats') || searchPhrase.trim().toLowerCase().endsWith('stats')) {
            if (searchPhrase.trim().toLowerCase() === 'stats' && isIrsPage && irsId) stats();
            else {
                const name = searchPhrase.trim().toLowerCase().replace('stats', '').trim() || defaultCurveName;
                socketEmit('/plots/search', { phrase: name, command: 'stats', tenors: [] });
            }
            return true;
        }

        if (searchPhrase.trim().toLowerCase().startsWith('curve') || searchPhrase.trim().toLowerCase().endsWith('curve')) {
            if (searchPhrase.trim().toLowerCase() === 'curve' && isIrsPage && irsId) curveCmd();
            else {
                const name = searchPhrase.trim().toLowerCase().replace('curve', '').trim() || defaultCurveName;
                socketEmit('/plots/search', { phrase: name, command: 'CURVE', tenors: [] });
            }
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('arc') || searchPhrase.trim().toLowerCase().endsWith('arc')) {
            if (searchPhrase.trim().toLowerCase() === 'arc' && isIrsPage && irsId) arc();
            else {
                const name = searchPhrase.trim().toLowerCase().replace('arc', '').trim() || defaultCurveName;
                socketEmit('/plots/search', { phrase: name, command: 'arc', tenors: [] });
            }
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('meet')) {
            const token = searchPhrase.trim().replace(/^meet\s*/i, '').trim();
            if (!token) {
                if (isIrsPage) meet({ irsId });
                else socketEmit('/plots/search', { phrase: 'aud ois', command: 'meet', tenors: [] });
            } else {
                socketEmit('/get/meet/irs-by-code', { code: token });
            }
            return true;
        }

        if (searchPhrase.trim().toLowerCase().startsWith('ove')
            && !searchPhrase.trim().toLowerCase().startsWith('over')) {
            const ccy = searchPhrase.trim().slice(3).trim().toUpperCase();
            const qs = ccy ? `?currency=${encodeURIComponent(ccy)}` : '';
            openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}fx-options/exotic${qs}`);
            setSearchPhrase('');
            resetCursor();
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('ovdv') || searchPhrase.trim().toLowerCase().endsWith('ovdv')) {
            // handle for command from fxo input form
            const name = searchPhrase.trim().toLowerCase().replace('ovdv', '').trim() || ovdvCurrency || 'USDINR';
            socketEmit('/get/fx-options/currency-pair', { currency: name, command: 'ovdv' });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('omon')) {
            const typed = searchPhrase.trim().toLowerCase().replace('omon', '').trim();
            if (typed) {
                socketEmit('/get/fx-options/currency-pair', { currency: typed, command: 'omon' });
            } else {
                getLastOmonCurrency().then((lastCurrency) => {
                    socketEmit('/get/fx-options/currency-pair', { currency: lastCurrency || 'USDINR', command: 'omon' });
                });
            }
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('ovml')) {
            const typed = searchPhrase.trim().toLowerCase().replace('ovml', '').trim();
            if (typed) {
                socketEmit('/get/fx-options/currency-pair', { currency: typed, command: 'ovml' });
            } else {
                ovmlCmd();
                setSearchPhrase('');
                resetCursor();
            }
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('dslice')) {
            const name = searchPhrase.trim().toLowerCase().replace('dslice', '').trim() || ovdvCurrency || 'USDINR';
            socketEmit('/get/fx-options/currency-pair', { currency: name, command: 'dslice' });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('rvol')) {
            const rest = searchPhrase.trim().slice(4).trim().toUpperCase();
            const tokens = rest.split(/\s+/).filter(Boolean);
            const TENOR_RE = /^\d+[DWMY]$/;
            let pair = null;
            let tenor = null;
            if (tokens.length === 1) {
                if (TENOR_RE.test(tokens[0])) {
                    tenor = tokens[0];
                    pair = (ovdvCurrency || '').toUpperCase() || null;
                } else {
                    pair = tokens[0];
                }
            } else if (tokens.length >= 2) {
                pair = tokens[0];
                tenor = tokens[1];
            } else {
                pair = (ovdvCurrency || '').toUpperCase() || null;
            }
            if (!tenor || !TENOR_RE.test(tenor)) {
                toast('RVOL needs a tenor (e.g. RVOL USDJPY 3M, RVOL 1Y)', { type: 'error', toastId: 'rvol-args' });
                return true;
            }
            if (!pair) {
                toast('RVOL needs a currency pair — no pair in context', { type: 'error', toastId: 'rvol-args' });
                return true;
            }
            const { bare, valid } = normalizeFxCurrency(pair);
            if (!valid) {
                toast(`RVOL: invalid currency "${pair}"`, { type: 'error', toastId: 'rvol-args' });
                return true;
            }
            openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}rvol/${bare.toLowerCase()}?tenor=${tenor}`);
            setSearchPhrase('');
            resetCursor();
            return true;
        }

        return false;
    }, [
        getHighlightedItemData,
        isIrsPage,
        isBondPage,
        curve,
        validImm,
        auth,
        forDetailsPage,
        isOTCPage,
        legs,
        immFwd,
        hasSpreadDiffStartDate,
        irsTerm,
        ovdvCurrency,
        irs,
        irsId,
        valid6s3sBobCcySet,
        setSearchPhrase,
        setShowLoader,
        resetCursor,
        isCorpChildComponent,
        setTicker,
        basisReferenceIrsId,
        plot,
        plotF,
        fwcm,
        edge,
        stats,
        curveCmd,
        arc,
        chronos,
        anchorCmd,
        basis,
        gip,
        otc,
        meet,
        ovmlCmd,
        runCommand
    ]);

    return { executeIrsEnterCommand, runCommand };
}
